"use client"

import { useEffect, useState } from 'react'
import { Sun, Moon, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { applyTheme, getCurrentTheme, oppositeTheme, themeFamily, THEMES, DEFAULT_THEME, THEME_EVENT, type Theme } from '@/lib/theme'
import { useT } from '@/lib/i18n'

/** The theme on <html>, kept current when another switch (or the first-run
 *  screen fit) changes it — the switches used to read it once, on mount. */
function useLiveTheme(): [Theme, (t: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(DEFAULT_THEME)
  useEffect(() => {
    const sync = () => setTheme(getCurrentTheme())
    sync()
    window.addEventListener(THEME_EVENT, sync)
    return () => window.removeEventListener(THEME_EVENT, sync)
  }, [])
  return [theme, setTheme]
}

/** Compact icon-only switch — for the header, where space is tight.
 *  Flips between the light and dark FAMILIES, bringing back the palette
 *  last chosen in each (Settings → გარეგნობა picks the palette). */
export function ThemeToggleCompact({ className }: { className?: string }) {
  const t = useT()
  const [theme, setTheme] = useLiveTheme()
  const dark = themeFamily(theme) === 'dark'

  const toggle = () => {
    const next = oppositeTheme(theme)
    setTheme(next)
    applyTheme(next)
  }

  return (
    <button
      onClick={toggle}
      title={dark ? t('theme.toLight') : t('theme.toDark')}
      aria-label={t('theme.toggle')}
      className={cn(
        'size-9 rounded-full md:rounded-xl border border-border bg-card dark:bg-white/5 hover:bg-accent transition-colors duration-150 flex items-center justify-center active:scale-[0.94] [-webkit-tap-highlight-color:transparent]',
        className,
      )}
    >
      {dark
        ? <Moon className="size-4 text-indigo-300" />
        : <Sun className="size-4 text-amber-500" />}
    </button>
  )
}

/** Full labeled pill switch — light family ↔ dark family. */
export function ThemeToggleSwitch({ className }: { className?: string }) {
  const t = useT()
  const [theme, setTheme] = useLiveTheme()
  const dark = themeFamily(theme) === 'dark'

  const toggle = () => {
    const next = oppositeTheme(theme)
    setTheme(next)
    applyTheme(next)
  }

  return (
    <button
      onClick={toggle}
      aria-label={t('theme.toggle')}
      className={cn(
        // flex + justify (not absolute+translate) so the knob can never
        // math its way past the track edge, regardless of border/padding
        'h-10 w-[72px] rounded-full border border-border bg-muted flex items-center p-1 transition-colors shrink-0',
        dark ? 'justify-end' : 'justify-start',
        className,
      )}
    >
      <span className="size-8 rounded-full bg-card dark:bg-slate-700 shadow-md flex items-center justify-center transition-transform duration-300 ease-out">
        {dark
          ? <Moon className="size-4 text-indigo-300" />
          : <Sun className="size-4 text-amber-500" />}
      </span>
    </button>
  )
}

/**
 * The palette picker (Settings → გარეგნობა). Eleven swatches, each drawn with
 * the palette's own page / card / ink / accent colours, so the choice is
 * made by looking, not by reading. Per device — a dark bar terminal and a
 * bright shop window can differ, exactly as Toast lets them.
 */
const SWATCH: Record<Theme, { page: string; card: string; ink: string; muted: string; accent: string }> = {
  light:    { page: 'oklch(0.925 0.009 75)',  card: 'oklch(0.970 0.006 75)',  ink: 'oklch(0.21 0.012 60)',  muted: 'oklch(0.40 0.014 60)',  accent: 'oklch(0.55 0.2 262)' },
  slate:    { page: 'oklch(0.955 0.006 250)', card: 'oklch(0.992 0.002 250)', ink: 'oklch(0.24 0.02 258)',  muted: 'oklch(0.44 0.02 258)',  accent: 'oklch(0.55 0.2 262)' },
  mist:     { page: 'oklch(0.905 0.018 255)', card: 'oklch(0.966 0.010 255)', ink: 'oklch(0.27 0.055 262)', muted: 'oklch(0.43 0.04 258)',  accent: 'oklch(0.50 0.13 255)' },
  sage:     { page: 'oklch(0.945 0.012 160)', card: 'oklch(0.988 0.005 160)', ink: 'oklch(0.23 0.02 170)',  muted: 'oklch(0.43 0.03 170)',  accent: 'oklch(0.52 0.12 170)' },
  sand:     { page: 'oklch(0.935 0.018 80)',  card: 'oklch(0.985 0.010 85)',  ink: 'oklch(0.24 0.02 55)',   muted: 'oklch(0.43 0.03 55)',   accent: 'oklch(0.52 0.14 45)' },
  cherry:   { page: 'oklch(0.940 0.018 350)', card: 'oklch(0.988 0.008 350)', ink: 'oklch(0.23 0.035 350)', muted: 'oklch(0.42 0.04 350)',  accent: 'oklch(0.50 0.18 10)' },
  graphite: { page: 'oklch(0.29 0.010 258)',  card: 'oklch(0.345 0.012 258)', ink: 'oklch(0.95 0.005 250)', muted: 'oklch(0.76 0.012 258)', accent: 'oklch(0.60 0.17 262)' },
  ocean:    { page: 'oklch(0.29 0.03 225)',   card: 'oklch(0.345 0.032 225)', ink: 'oklch(0.95 0.008 210)', muted: 'oklch(0.76 0.02 215)',  accent: 'oklch(0.55 0.13 200)' },
  amethyst: { page: 'oklch(0.24 0.08 287)',   card: 'oklch(0.30 0.085 287)',  ink: 'oklch(0.95 0.01 280)',  muted: 'oklch(0.84 0.075 252)', accent: 'oklch(0.55 0.20 293)' },
  darkcherry: { page: 'oklch(0.22 0.06 15)',  card: 'oklch(0.28 0.075 14)',  ink: 'oklch(0.95 0.012 10)', muted: 'oklch(0.84 0.09 5)',    accent: 'oklch(0.55 0.19 6)' },
  dark:     { page: 'oklch(0.20 0.014 258)',  card: 'oklch(0.26 0.016 258)',  ink: 'oklch(0.94 0.006 250)', muted: 'oklch(0.68 0.02 258)',  accent: 'oklch(0.68 0.19 262)' },
}

export function ThemePicker() {
  const tr = useT()
  const [theme, setTheme] = useLiveTheme()

  const choose = (t: Theme) => { setTheme(t); applyTheme(t) }

  // Two families, named: eleven cards in one grid read as a jumble; six
  // light and five dark under their own headings read as a choice.
  const families: { key: 'light' | 'dark'; label: string }[] = [
    { key: 'light', label: tr('theme.toggle.1') },
    { key: 'dark', label: tr('theme.toggle.2') },
  ]
  return (
    <div className="flex flex-col gap-5">
      {families.map((f) => (
        <div key={f.key} className="flex flex-col gap-2.5">
          <p className="text-[12px] font-bold uppercase tracking-[0.08em] text-muted-foreground px-0.5">{f.label}</p>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {THEMES.filter((t) => t.family === f.key).map((t) => {
        const sw = SWATCH[t.key]
        const on = theme === t.key
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => choose(t.key)}
            aria-pressed={on}
            className={cn(
              'press rounded-2xl border bg-card p-3 text-left transition-colors flex flex-col gap-3',
              on ? 'border-primary ring-2 ring-primary/15' : 'border-border hover:border-primary/30',
            )}
          >
            {/* a miniature of the palette: page, a card with two text lines and a button */}
            <div className="rounded-xl p-3 flex flex-col gap-2" style={{ background: sw.page }}>
              <div className="rounded-lg p-2.5 flex flex-col gap-1.5" style={{ background: sw.card, boxShadow: '0 1px 2px oklch(0 0 0 / 0.08)' }}>
                <div className="h-2 w-2/3 rounded" style={{ background: sw.ink }} />
                <div className="h-1.5 w-1/2 rounded" style={{ background: sw.muted, opacity: 0.7 }} />
                <div className="h-4 w-14 rounded-md mt-1" style={{ background: sw.accent }} />
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-bold text-foreground leading-tight">{tr(t.label)}</span>
                <span className="block text-[12px] text-muted-foreground leading-snug mt-0.5 line-clamp-2">{tr(t.hint)}</span>
              </span>
              {on && <Check className="size-4 text-primary shrink-0 mt-0.5" />}
            </div>
          </button>
        )
      })}
          </div>
        </div>
      ))}
    </div>
  )
}
